// An invoice on A4 paper, in the six templates the phone app prints
// (packages/paidlark_core/lib/services/pdf/invoice_pdf_service.dart):
// Classic, Bold, Minimal and Modern, plus Elegant and Sidebar. Sizes are in
// points like the PDF's, so the page prints at exactly A4. Always white
// paper, in dark mode too. "Save PDF" is the browser's own print to PDF.
import { type CSSProperties, type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { bankIsEmpty, type Business, businessInitials } from '@/core/business';
import { formatDate } from '@/core/dates';
import { fontOf, onBrand, templateOf } from '@/core/design';
import { currencyOfInvoice, type Invoice, itemAmount, taxLabel, totalsOf } from '@/core/invoice';
import { formatQuantity, Money } from '@/core/money';
import { clientNameLabel, discountLabel } from '@/l10n/labels';
import { t } from '@/l10n/strings';
import './paper.css';

interface Props {
  invoice: Invoice;
  business: Business;
  /** Pro may remove the "Made with Paidlark" line; free invoices always carry it. */
  isPro: boolean;
}

const hex = (argb: number) => '#' + (argb & 0xffffff).toString(16).padStart(6, '0');

/** The brand colour mixed 12% into white, for tinted fills. */
function tint(argb: number) {
  const mix = (c: number) => Math.round(255 - (255 - c) * 0.12);
  return `rgb(${mix((argb >> 16) & 0xff)}, ${mix((argb >> 8) & 0xff)}, ${mix(argb & 0xff)})`;
}

export function InvoicePaper({ invoice, business, isPro }: Props) {
  const template = templateOf(invoice.design.templateId).id;
  const font = fontOf(invoice.design.fontId);
  const brand = hex(invoice.design.brandColor);
  const style = {
    '--brand': brand,
    '--on-brand': onBrand(invoice.design.brandColor),
    '--tint': tint(invoice.design.brandColor),
    '--font': `'inv-${font.file}', 'inv-plus-jakarta-sans', system-ui, sans-serif`,
    '--display': `'inv-${font.display ?? font.file}', 'inv-plus-jakarta-sans', Georgia, serif`,
    '--display-weight': font.displayWeight ?? font.heavy,
    '--heavy': font.heavy,
  } as CSSProperties;
  const parts = { invoice, business, isPro };
  return (
    <article className={`paper paper--${template}`} style={style} aria-label={t('pdfInvoiceNumber', { number: invoice.number })}>
      {template === 'sidebar' && (
        <aside className="paper__side">
          <BusinessBlock {...parts} onColour logoSize={40} />
        </aside>
      )}
      <div className="paper__body">
        <Header {...parts} template={template} />
        <BilledTo invoice={invoice} />
        <ItemsTable invoice={invoice} />
        <div className="paper__row paper__row--top" style={{ marginTop: '14pt' }}>
          <Notes invoice={invoice} />
          <Totals invoice={invoice} outlined={template === 'elegant'} />
        </div>
        {!bankIsEmpty(business.bank) && <PayTo invoice={invoice} business={business} />}
        {invoice.design.showSignature && business.signature && <Signature business={business} />}
        {(!isPro || invoice.design.showPaidlarkLine) && <MadeWith />}
      </div>
    </article>
  );
}

function Header({ invoice, business, isPro, template }: Props & { template: string }) {
  const totals = totalsOf(invoice);
  switch (template) {
    case 'bold':
      return (
        <div className="paper__bold-head">
          <BusinessBlock invoice={invoice} business={business} isPro={isPro} onColour />
          <Meta invoice={invoice} title={t('pdfInvoiceHeading')} titleClass="paper__heavy-title" onColour />
        </div>
      );
    case 'minimal':
      return (
        <>
          <div className="paper__row paper__row--top">
            <BusinessBlock invoice={invoice} business={business} isPro={isPro} />
            <Meta invoice={invoice} titleClass="paper__display paper__display--24" plain />
          </div>
          <hr className="paper__rule" style={{ marginTop: '16pt' }} />
        </>
      );
    case 'modern': {
      const paid = totals.balance.isZero;
      return (
        <div className="paper__modern-head">
          <BusinessBlock invoice={invoice} business={business} isPro={isPro} />
          <div className="paper__end">
            <span className="paper__label" style={{ color: 'var(--brand)' }}>
              {paid ? t('statusPaid') : t('pdfAmountDue')}
            </span>
            <span className="paper__heavy" style={{ fontSize: '24pt', color: 'var(--brand)', marginTop: '4pt' }}>
              {(paid ? totals.total : totals.balance).format()}
            </span>
            <span className="paper__strong" style={{ fontSize: '10pt', marginTop: '8pt' }}>
              {t('pdfInvoiceNumber', { number: invoice.number })}
            </span>
            <span className="paper__muted" style={{ fontSize: '8.5pt', marginTop: '2pt' }}>
              {t('pdfIssuedDue', { issueDate: formatDate(invoice.issueDate), dueDate: formatDate(invoice.dueDate) })}
            </span>
          </div>
        </div>
      );
    }
    case 'elegant':
      return (
        <div className="paper__center">
          <Logo invoice={invoice} business={business} size={40} />
          <span className="paper__strong" style={{ fontSize: '13pt', marginTop: '8pt' }}>
            {business.name || t('pdfYourBusiness')}
          </span>
          {contactLines(business).length > 0 && (
            <span className="paper__muted" style={{ fontSize: '8.5pt' }}>
              {contactLines(business).join('  ·  ')}
            </span>
          )}
          <span className="paper__display" style={{ fontSize: '30pt', color: 'var(--brand)', marginTop: '18pt' }}>
            {t('pdfInvoiceTitle')}
          </span>
          <span className="paper__muted" style={{ fontSize: '9pt', marginTop: '4pt' }}>
            {t('pdfNumberIssuedDue', { number: invoice.number, issueDate: formatDate(invoice.issueDate), dueDate: formatDate(invoice.dueDate) })}
          </span>
          <hr className="paper__rule paper__rule--brand" style={{ marginTop: '14pt', width: '100%' }} />
        </div>
      );
    case 'sidebar':
      return (
        <div className="stack-1">
          <span className="paper__display" style={{ fontSize: '30pt', color: 'var(--brand)' }}>
            {t('pdfInvoiceTitle')}
          </span>
          <span className="paper__strong paper__muted" style={{ fontSize: '10pt', marginTop: '4pt' }}>
            {invoice.number}
          </span>
          <span className="paper__muted" style={{ fontSize: '9pt' }}>
            {t('pdfIssuedDue', { issueDate: formatDate(invoice.issueDate), dueDate: formatDate(invoice.dueDate) })}
          </span>
        </div>
      );
    default:
      return (
        <>
          <div className="paper__band" />
          <div className="paper__row paper__row--top" style={{ marginTop: '20pt' }}>
            <BusinessBlock invoice={invoice} business={business} isPro={isPro} />
            <Meta invoice={invoice} titleClass="paper__display paper__display--26" brandTitle />
          </div>
        </>
      );
  }
}

const contactLines = (b: Business) => [b.address, b.email, b.phone].map((s) => s.trim()).filter(Boolean);

function Logo({ invoice, business, size, onColour }: { invoice: Invoice; business: Business; size: number; onColour?: boolean }) {
  if (invoice.design.showLogo && business.logo) {
    return <img className="paper__logo" src={business.logo} alt="" style={{ width: `${size}pt`, height: `${size}pt` }} />;
  }
  // On a brand-coloured ground the initials tile swaps colours to show.
  return (
    <span
      className="paper__monogram"
      style={{
        width: `${size}pt`,
        height: `${size}pt`,
        borderRadius: `${size * 0.28}pt`,
        fontSize: `${size * 0.36}pt`,
        background: onColour ? 'var(--on-brand)' : 'var(--brand)',
        color: onColour ? 'var(--brand)' : 'var(--on-brand)',
      }}
    >
      {businessInitials(business.name)}
    </span>
  );
}

function BusinessBlock({ invoice, business, onColour, logoSize = 36 }: Props & { onColour?: boolean; logoSize?: number }) {
  return (
    <div className="paper__business" style={onColour ? { color: 'var(--on-brand)' } : undefined}>
      <Logo invoice={invoice} business={business} size={logoSize} onColour={onColour} />
      <span className="paper__heavy" style={{ fontSize: '12pt', marginTop: '8pt' }}>
        {business.name || t('pdfYourBusiness')}
      </span>
      {contactLines(business).map((line) => (
        <span key={line} className={onColour ? undefined : 'paper__muted'} style={{ fontSize: '8.5pt', marginTop: '2pt', whiteSpace: 'pre-line' }}>
          {line}
        </span>
      ))}
    </div>
  );
}

function Meta({ invoice, title, titleClass, brandTitle, onColour, plain }: { invoice: Invoice; title?: string; titleClass: string; brandTitle?: boolean; onColour?: boolean; plain?: boolean }) {
  const colour = onColour ? 'var(--on-brand)' : undefined;
  return (
    <div className="paper__end" style={{ color: colour }}>
      <span className={titleClass} style={{ color: brandTitle ? 'var(--brand)' : plain ? undefined : colour }}>
        {title ?? t('pdfInvoiceTitle')}
      </span>
      <span className={`paper__strong${onColour ? '' : ' paper__muted'}`} style={{ fontSize: '9pt', marginTop: '6pt' }}>
        #{invoice.number}
      </span>
      <span className={onColour ? undefined : 'paper__muted'} style={{ fontSize: '9pt', marginTop: '2pt' }}>
        {t('pdfIssuedOn', { issueDate: formatDate(invoice.issueDate) })}
      </span>
      <span className={`paper__strong${onColour ? '' : ' paper__muted'}`} style={{ fontSize: '9pt', marginTop: '2pt' }}>
        {t('pdfDueOn', { dueDate: formatDate(invoice.dueDate) })}
      </span>
    </div>
  );
}

function BilledTo({ invoice }: { invoice: Invoice }) {
  const c = invoice.client;
  return (
    <div className="paper__billed">
      <span className="paper__label">{t('pdfBilledTo')}</span>
      <span className="paper__strong" style={{ fontSize: '11pt', marginTop: '4pt' }}>
        {clientNameLabel(c)}
      </span>
      {[c.email, c.phone, c.address]
        .filter((s) => s.trim())
        .map((line) => (
          <span key={line} className="paper__muted" style={{ fontSize: '9pt', marginTop: '2pt', whiteSpace: 'pre-line' }}>
            {line.trim()}
          </span>
        ))}
    </div>
  );
}

function ItemsTable({ invoice }: { invoice: Invoice }) {
  const currency = currencyOfInvoice(invoice);
  return (
    <table className="paper__items">
      <thead>
        <tr>
          <th>{t('pdfItem')}</th>
          <th className="end">{t('pdfQty')}</th>
          <th className="end">{t('pdfRateColumn', { symbol: currency.symbol })}</th>
          <th className="end">{t('pdfAmountColumn', { symbol: currency.symbol })}</th>
        </tr>
      </thead>
      <tbody>
        {invoice.items.map((item) => (
          <tr key={item.id}>
            <td>
              <span className="paper__strong" style={{ fontSize: '9.5pt', display: 'block' }}>
                {item.name}
              </span>
              {item.description.trim() && (
                <span className="paper__muted" style={{ fontSize: '8pt', display: 'block', marginTop: '2pt' }}>
                  {item.description.trim()}
                </span>
              )}
            </td>
            <td className="end">{formatQuantity(item.quantity)}</td>
            <td className="end">{new Money(item.rateMinor, currency).format({ withSymbol: false })}</td>
            <td className="end paper__strong">{itemAmount(item, currency).format({ withSymbol: false })}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Totals({ invoice, outlined }: { invoice: Invoice; outlined: boolean }) {
  const totals = totalsOf(invoice);
  const paid = totals.paid.isPositive;
  const row = (label: ReactNode, value: string) => (
    <div className="paper__total-row">
      <span className="paper__muted">{label}</span>
      <span className="paper__strong">{value}</span>
    </div>
  );
  const due = paid ? totals.balance : totals.total;
  return (
    <div className="paper__totals">
      {row(t('pdfSubtotal'), totals.subtotal.format())}
      {invoice.discount.type !== 'none' && row(discountLabel(invoice.discount), totals.discount.negate().format())}
      {invoice.tax.basisPoints > 0 && row(taxLabel(invoice.tax), totals.tax.format())}
      {paid && (
        <>
          {row(t('welcomeTotal'), totals.total.format())}
          {row(t('statusPaid'), totals.paid.negate().format())}
        </>
      )}
      <div className={outlined ? 'paper__due paper__due--outlined' : 'paper__due'}>
        <span>{paid ? t('pdfBalanceDue') : t('welcomeTotal')}</span>
        <span style={{ fontSize: '13pt' }}>{due.format()}</span>
      </div>
    </div>
  );
}

function Notes({ invoice }: { invoice: Invoice }) {
  const notes = invoice.notes.trim();
  if (!notes) return <div className="grow" />;
  return (
    <div className="grow" style={{ display: 'flex', flexDirection: 'column' }}>
      <span className="paper__label">{t('pdfNotes')}</span>
      <span style={{ fontSize: '9pt', marginTop: '4pt', whiteSpace: 'pre-line', lineHeight: 1.7 }}>{notes}</span>
    </div>
  );
}

function PayTo({ invoice, business }: { invoice: Invoice; business: Business }) {
  const bank = business.bank;
  const account = [bank.bankName, bank.accountNumber].filter((s) => s.trim()).join('  ·  ');
  return (
    <div className="paper__payto">
      <span className="paper__label">{t('pdfPayTo')}</span>
      {account && (
        <span className="paper__strong" style={{ fontSize: '9.5pt', marginTop: '4pt' }}>
          {account}
        </span>
      )}
      {bank.accountName.trim() && <span style={{ fontSize: '9pt', marginTop: '2pt' }}>{t('pdfAccountName', { accountName: bank.accountName.trim() })}</span>}
      {invoice.design.showPaymentReference && (
        <span className="paper__muted" style={{ fontSize: '9pt', marginTop: '2pt' }}>
          {t('pdfPaymentReference', { number: invoice.number })}
        </span>
      )}
    </div>
  );
}

function Signature({ business }: { business: Business }) {
  return (
    <div className="paper__signature">
      <img src={business.signature!} alt="" />
      <hr />
      <span className="paper__label">{t('pdfAuthorisedSignature')}</span>
      <span className="paper__strong" style={{ fontSize: '9pt', marginTop: '2pt' }}>
        {business.name}
      </span>
    </div>
  );
}

/** "Made with Paidlark", with the P-lark in Paidlark forest whatever the invoice's colour. */
function MadeWith() {
  return (
    <div className="paper__made">
      <svg viewBox="0 0 64 64" aria-hidden>
        <path d="M19 57 V13 H34 C44.5 13 50 19.5 50 27.5 C50 35.5 44.5 42 34 42 H19" fill="none" stroke="#0F4A37" strokeWidth="10" strokeLinejoin="round" strokeLinecap="round" />
        <path d="M53 22 L63 27.5 L53 33 Z" fill="#0F4A37" />
      </svg>
      {t('pdfMadeWithPaidlark')}
    </div>
  );
}

/**
 * Shows the paper scaled to fit its container's width, as a picture of the
 * printed page. The paper itself stays A4, so printing is exact.
 */
export function ScaledPaper(props: Props & { maxWidth?: number }) {
  const box = useRef<HTMLDivElement>(null);
  const paper = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    const outer = box.current;
    const inner = paper.current;
    if (!outer || !inner) return;
    const fit = () => {
      const width = inner.offsetWidth;
      if (!width) return;
      const s = Math.min(outer.clientWidth, props.maxWidth ?? Infinity) / width;
      setScale(s);
      setHeight(inner.offsetHeight * s);
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(outer);
    observer.observe(inner);
    return () => observer.disconnect();
  }, [props.maxWidth]);
  return (
    <div ref={box} className="paper-frame" style={{ height: height || undefined }}>
      <div ref={paper} className="paper-scaler" style={{ transform: `scale(${scale})`, visibility: scale ? 'visible' : 'hidden' }}>
        <InvoicePaper invoice={props.invoice} business={props.business} isPro={props.isPro} />
      </div>
    </div>
  );
}

